import {
  isPoojaBookPathname as isPoojaBookPathnameFromRoutes,
  isPoojaDetailPathname as isPoojaDetailPathnameFromRoutes,
  buildPoojaDetailPathname,
  buildPoojaBookPathname,
} from './poojaRoutes';
import { buildMarketingContinuationSearch } from './utm';

export function isPoojaBookPathname(pathname) {
  return isPoojaBookPathnameFromRoutes(pathname);
}

export function isPoojaDetailPathname(pathname) {
  return isPoojaDetailPathnameFromRoutes(pathname);
}

export const BOOK_IN_APP_VIEW_PARAM = 'view';
export const BOOK_IN_APP_VIEW_VALUE = 'inappview';

function getSearchParam(params, key) {
  const direct = params.get(key);
  if (direct != null && direct !== '') return direct;
  for (const [k, v] of params.entries()) {
    if (k.trim() === key) return v ?? '';
  }
  return '';
}

export function hasInAppViewParam(search = '') {
  const params = new URLSearchParams(search || '');
  const view = getSearchParam(params, BOOK_IN_APP_VIEW_PARAM).trim().toLowerCase();
  return view === BOOK_IN_APP_VIEW_VALUE;
}

/** Kept for callers that sync route state; hiding is based only on the current URL. */
export function syncInAppViewSession(search = '') {
  void search;
}

/** True only when the current URL has `view=inappview`. */
export function isInAppViewActive(search = '') {
  return hasInAppViewParam(search);
}

/**
 * Hide site header, bottom nav, footer, FABs, guru banner only when current URL has `view=inappview`.
 */
export function shouldHideMobileChromeInAppView(_pathname, search = '') {
  return isInAppViewActive(search);
}

/**
 * Book page with in-app view — sticky Pay Now, Pooja Info topbar, etc.
 */
export function shouldHideMobileChromeOnBookPage(pathname, search = '') {
  if (!isPoojaBookPathname(pathname)) return false;
  return isInAppViewActive(search);
}

const IN_APP_VIEW_PASSTHROUGH_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'package_num',
  'coupon',
  'couponCode',
  'coupon_code',
];

/** Keep `?view=inappview` (and utm/package params) when moving between detail ↔ book. */
export function buildInAppViewContinuationSearch(currentSearch = '') {
  if (!isInAppViewActive(currentSearch)) return '';
  const raw = String(currentSearch || '');
  const incoming = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const out = new URLSearchParams();
  out.set(BOOK_IN_APP_VIEW_PARAM, BOOK_IN_APP_VIEW_VALUE);
  for (const key of IN_APP_VIEW_PASSTHROUGH_KEYS) {
    const v = getSearchParam(incoming, key);
    if (v) out.set(key, v);
  }
  return `?${out.toString()}`;
}

/**
 * UTM + `view=inappview` for book → pay → thank-you navigations.
 */
export function buildBookingFlowContinuationSearch(currentSearch = '', lang) {
  const base = buildMarketingContinuationSearch(currentSearch, lang);
  if (!isInAppViewActive(currentSearch)) return base;
  const params = new URLSearchParams(base.startsWith('?') ? base.slice(1) : '');
  params.set(BOOK_IN_APP_VIEW_PARAM, BOOK_IN_APP_VIEW_VALUE);
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Merge booking-flow query params (incl. view=inappview) into URLSearchParams. */
export function applyBookingFlowSearchParams(params, currentSearch = '', lang) {
  const cont = buildBookingFlowContinuationSearch(currentSearch, lang);
  const merged = new URLSearchParams(cont.startsWith('?') ? cont.slice(1) : cont);
  for (const [k, v] of merged.entries()) {
    params.set(k, v);
  }
  return params;
}

/** Thank-you refresh: keep person-details step open for personalized pooja bookings. */
export function buildThankYouEditUsersSearch(currentSearch = '', lang) {
  const base = buildBookingFlowContinuationSearch(currentSearch, lang);
  const params = new URLSearchParams(base.startsWith('?') ? base.slice(1) : base);
  params.set('edit', 'users');
  const s = params.toString();
  return s ? `?${s}` : '';
}

const THANK_YOU_FORM_EDIT = new Set(['persons', 'users']);

/** Strip accidental `/edit=…` suffixes glued onto UTM values (breaks Meta/Facebook attribution). */
function stripEmbeddedEditFromParamValue(value) {
  return String(value || '')
    .replace(/\/edit=(users|persons|1|true|0|false)\/?$/i, '')
    .trim();
}

function readThankYouSearchParams(search = '') {
  const raw = String(search || '');
  return new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
}

/** Raw `edit` query value (lowercase), or '' when absent / edit=0. */
export function readThankYouEditQueryValue(search = '') {
  return String(readThankYouSearchParams(search).get('edit') ?? '').trim().toLowerCase();
}

/**
 * Opens the devotee form from `?edit=users` or `?edit=persons` (profile / Your details).
 * `?edit=1` also opens the form on thank-you when not coming direct from payment.
 */
export function isThankYouEditPersonsSearch(search = '') {
  const direct = readThankYouEditQueryValue(search);
  return THANK_YOU_FORM_EDIT.has(direct);
}

/** Direct payment landing — clean UTM only, never add `edit`. */
export function buildThankYouFreshPaymentSearch(currentSearch = '', lang) {
  const params = readThankYouSearchParams(currentSearch);

  for (const [key, value] of [...params.entries()]) {
    const cleaned = stripEmbeddedEditFromParamValue(value);
    if (cleaned) params.set(key, cleaned);
    else params.delete(key);
  }
  params.delete('edit');

  const marketing = buildBookingFlowContinuationSearch(`?${params.toString()}`, lang);
  const next = new URLSearchParams(marketing.startsWith('?') ? marketing.slice(1) : marketing);
  next.delete('edit');
  const s = next.toString();
  return s ? `?${s}` : '';
}

/** Copy/paste, refresh, or Facebook — keep UTM tags and set `edit=1` (not inside utm_source). */
export function buildThankYouClosedSearch(currentSearch = '', lang) {
  const params = readThankYouSearchParams(currentSearch);

  for (const [key, value] of [...params.entries()]) {
    const cleaned = stripEmbeddedEditFromParamValue(value);
    if (cleaned) params.set(key, cleaned);
    else params.delete(key);
  }

  if (!isThankYouEditPersonsSearch(`?${params.toString()}`)) {
    params.set('edit', '1');
  }

  const marketing = buildBookingFlowContinuationSearch(`?${params.toString()}`, lang);
  const next = new URLSearchParams(marketing.startsWith('?') ? marketing.slice(1) : marketing);
  if (!isThankYouEditPersonsSearch(`?${next.toString()}`)) {
    next.set('edit', '1');
  }
  const s = next.toString();
  return s ? `?${s}` : '';
}

/** After save or summary — `edit=1` for Meta; use `edit=users` only when editing the form. */
export function buildThankYouClearEditSearch(currentSearch = '', lang) {
  const params = readThankYouSearchParams(currentSearch);

  for (const [key, value] of [...params.entries()]) {
    const cleaned = stripEmbeddedEditFromParamValue(value);
    if (cleaned) params.set(key, cleaned);
    else params.delete(key);
  }

  params.set('edit', '1');
  const marketing = buildBookingFlowContinuationSearch(`?${params.toString()}`, lang);
  const next = new URLSearchParams(marketing.startsWith('?') ? marketing.slice(1) : marketing);
  next.set('edit', '1');
  const s = next.toString();
  return s ? `?${s}` : '';
}

/**
 * Fix Facebook / in-app links like `?utm_source=Daiv-app/edit=1` → `?utm_source=Daiv-app&edit=1`.
 * Preserves an explicit `?edit=users` when the devotee form is being edited.
 */
export function sanitizeThankYouMarketingSearch(search = '', lang) {
  const params = readThankYouSearchParams(search);
  const formEdit = isThankYouEditPersonsSearch(search);
  let hadEmbeddedEdit = false;

  for (const [key, value] of [...params.entries()]) {
    const cleaned = stripEmbeddedEditFromParamValue(value);
    if (cleaned !== String(value || '')) {
      hadEmbeddedEdit = true;
      if (cleaned) params.set(key, cleaned);
      else params.delete(key);
    }
  }

  if (hadEmbeddedEdit && !formEdit) {
    params.set('edit', '1');
  }

  const rawNorm = search.startsWith('?') ? search : search ? `?${search}` : '';
  const marketing = buildBookingFlowContinuationSearch(`?${params.toString()}`, lang);
  const next = new URLSearchParams(marketing.startsWith('?') ? marketing.slice(1) : marketing);
  if (formEdit) {
    next.set('edit', readThankYouEditQueryValue(search) || 'users');
  } else if (hadEmbeddedEdit || readThankYouEditQueryValue(search) === '1') {
    next.set('edit', '1');
  }
  const canonical = next.toString() ? `?${next.toString()}` : '';
  if (canonical === rawNorm) return null;
  if (!hadEmbeddedEdit && !formEdit && readThankYouEditQueryValue(search) === '1') return null;
  return canonical;
}

export function buildPoojaDetailLocation(slug, search = '', scheduleSegment = null) {
  if (!slug) return { pathname: '/', search: '' };
  return {
    pathname: buildPoojaDetailPathname(slug, scheduleSegment),
    // Always keep UTM/campaign tags (Cubera/Meta); in-app view param added when present.
    search: buildBookingFlowContinuationSearch(search),
  };
}

export function buildPoojaBookLocation(slug, search = '', scheduleSegment = null) {
  if (!slug) return { pathname: '/poojas', search: '' };
  return {
    pathname: buildPoojaBookPathname(slug, scheduleSegment),
    // Always keep UTM/campaign tags so Cubera/Meta don't become "Direct" after Book Now.
    search: buildBookingFlowContinuationSearch(search),
  };
}
